import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import { resolveMediaDisplayUrl } from '@/features/studio/lib/media-display'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { cn } from '@/shared/lib/utils'

export function ImagePreviewModal({
  open,
  urls,
  index,
  onClose,
  onIndexChange,
  title,
}: {
  open: boolean
  urls: string[]
  index: number
  onClose: () => void
  onIndexChange: (index: number) => void
  title?: string
}) {
  const { t } = useTranslation()
  const safeIndex = urls.length > 0 ? Math.min(Math.max(index, 0), urls.length - 1) : 0
  const current = urls[safeIndex] ?? ''

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && urls.length > 1) {
        onIndexChange(safeIndex <= 0 ? urls.length - 1 : safeIndex - 1)
      }
      if (e.key === 'ArrowRight' && urls.length > 1) {
        onIndexChange(safeIndex >= urls.length - 1 ? 0 : safeIndex + 1)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onIndexChange, safeIndex, urls.length])

  if (!current) return null

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        showCloseButton
        className="flex max-h-[min(88vh,900px)] w-full max-w-5xl flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl"
      >
        <DialogHeader className="flex-row items-start justify-between border-b px-4 py-3 text-left">
          <div className="min-w-0 pr-4">
            <DialogTitle className="truncate text-sm font-medium">
              {title ?? t('bible.imagePreview')}
            </DialogTitle>
            {urls.length > 1 ? (
              <p className="text-xs text-muted-foreground">
                {t('bible.imagePreviewCounter', { current: safeIndex + 1, total: urls.length })}
              </p>
            ) : null}
          </div>
        </DialogHeader>

        <div className="relative flex min-h-0 flex-1 items-center justify-center bg-muted/30 p-4 sm:p-6">
          {urls.length > 1 ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                onClick={() => onIndexChange(safeIndex <= 0 ? urls.length - 1 : safeIndex - 1)}
                className="absolute left-3 z-10 rounded-full border-border bg-background/80 hover:bg-background"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                onClick={() => onIndexChange(safeIndex >= urls.length - 1 ? 0 : safeIndex + 1)}
                className="absolute right-3 z-10 rounded-full border-border bg-background/80 hover:bg-background"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </>
          ) : null}
          <img
            src={resolveMediaDisplayUrl(current)}
            alt=""
            className="max-h-[min(68vh,760px)] max-w-full rounded-lg object-contain"
          />
        </div>

        {urls.length > 1 ? (
          <div className="studio-scrollbar-hidden shrink-0 border-t px-4 py-3">
            <div className="flex justify-center gap-2 overflow-x-auto">
              {urls.map((url, i) => (
                <Button
                  key={`${url}-${i}`}
                  type="button"
                  variant="ghost"
                  onClick={() => onIndexChange(i)}
                  className={cn(
                    'h-14 w-14 shrink-0 overflow-hidden rounded-lg border p-0',
                    i === safeIndex ? 'border-indigo-500/60 ring-2 ring-indigo-500/30' : 'border-border',
                  )}
                >
                  <img src={resolveMediaDisplayUrl(url)} alt="" className="h-full w-full object-cover" />
                </Button>
              ))}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
